import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuthentication, useChannels } from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import { Channel, ChannelType, FlightRoomUserStatus, QueryKeys } from '@connect-x-app/shared-domain-models';
import { COLORS } from '@connect-x-app/shared-ui';
import { CHANNEL_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Button, Icon, SideModal, toastError } from '@connect-x-app/web-ui';
import { useQueryClient } from '@tanstack/react-query';

import { useOnlineCallback } from '../../hooks/useOnlineCallback';
import { UserInfoList } from './UserInfoList';

import './ChannelInfoSideModal.scss';

type Props = {
  channel: Channel;
  isVisible: boolean;
  onClose: () => void;
  roomId: string;
  showOfflineText?: boolean;
};

export const ChannelInfoSideModal = ({ channel, isVisible, onClose, roomId, showOfflineText }: Props) => {
  const { t } = useTranslation();
  const { user } = useAuthentication();
  const { leaveChannel } = useChannels();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const currentUserInChannel = user ? channel.users.find(u => u.externalId === user.externalId) : undefined;
  const canLeaveChannel = currentUserInChannel?.status === FlightRoomUserStatus.JOINED;

  const [isLeaving, setIsLeaving] = useState(false);

  const handleLeaveChannel = async () => {
    setIsLeaving(true);
    try {
      const result = await leaveChannel(roomId, channel.id);
      if (result?.ok) {
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.FLIGHTROOM, roomId] });
        navigate(`/dashboard/flightrooms/${roomId}/channel`);
        onClose();
      } else {
        toastError(t('CHANNEL.LEAVE.ERROR_ALERT.DESCRIPTION'));
      }
    } catch {
      toastError(t('CHANNEL.LEAVE.ERROR_ALERT.DESCRIPTION'));
    } finally {
      setIsLeaving(false);
    }
  };

  const handleLeaveButtonClick = useOnlineCallback(handleLeaveChannel, 'ACTIONS.LEAVE_CHANNEL');

  return (
    <SideModal
      bottomButtons={
        canLeaveChannel ? (
          <Button
            disabled={isLeaving}
            isOutline
            loading={isLeaving}
            onClick={handleLeaveButtonClick}
            theme="negative"
            type="button"
          >
            {t('CHANNEL.LEAVE.BUTTON')}
          </Button>
        ) : null
      }
      content={
        <>
          {channel.description ? (
            <div className="channel-info-modal__description">
              <Text className="channel-info-modal__section-title" color={COLORS.zulu.$08} size="SM" uppercase weight="bold">
                {t('CHANNEL.DETAIL.DESCRIPTION')}
              </Text>
              <div className="channel-info-modal__section-text">
                {channel.type === ChannelType.PRIVATE ? (
                  <Icon name="privateChannel" testId={CHANNEL_TEST_IDS.channels.privateIcon} />
                ) : null}
                <Text color={COLORS.zulu.$08} size="SM">
                  {channel.description}
                </Text>
              </div>
            </div>
          ) : null}
          <div className="channel-info-modal__participants">
            <Text className="channel-info-modal__section-title" color={COLORS.zulu.$08} size="SM" uppercase weight="bold">
              {t('CHANNEL.DETAIL.PARTICIPANTS')}
            </Text>
            <UserInfoList channel={channel} showOfflineText={showOfflineText} />
          </div>
        </>
      }
      header={{
        rightIcon: { iconName: 'closeCross', onClick: onClose },
        title: { text: t('CHANNEL.DETAIL.TITLE') },
      }}
      isVisible={isVisible}
      onBackgroundClick={onClose}
    />
  );
};
